import { bigIntParser } from "../utils/functions/bigIntParser";
import { endpointGenerator } from "../utils/functions/endpointGenerator";
import { type Execution } from "../utils/functions/execution";
import {
    type Chain,
    type GoldRushResponse,
} from "../utils/types/Generic.types";
import {
    type CheckOwnershipInNftQueryParamOpts,
    type GetNftsForAddressQueryParamOpts,
    type NftAddressBalanceNftResponse,
    type NftOwnershipForCollectionResponse,
} from "../utils/types/NftService.types";

/**
 * NFTs API
 *
 */
export class NftService {
    constructor(private execution: Execution) {}

    /**
     *
     * Commonly used to render the NFTs (including ERC721 and ERC1155) held by an address.
     *
     * **Credit Cost**: 1 per call
     *
     * @param {string} chainName - The chain name eg: `eth-mainnet`.
     * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {GetNftsForAddressQueryParamOpts} queryParamOpts
     *   - `noSpam`: If `true`, the suspected spam tokens are removed. Supports `eth-mainnet` and `matic-mainnet`.
     *   - `noNftAssetMetadata`: If `true`, the response shape is limited to a list of collections and token ids, omitting metadata and asset information. Helpful for faster response times and wallets holding a large number of NFTs.
     *
     */
    public async getNftsForAddress(
        chainName: Chain,
        walletAddress: string,
        queryParamOpts?: GetNftsForAddressQueryParamOpts
    ): Promise<GoldRushResponse<NftAddressBalanceNftResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/address/${walletAddress}/balances_nft`,
            [
                {
                    key: "no-spam",
                    value: queryParamOpts?.noSpam,
                },
                {
                    key: "no-nft-asset-metadata",
                    value: queryParamOpts?.noNftAssetMetadata,
                },
            ]
        );

        const parseData = (
            data: GoldRushResponse<NftAddressBalanceNftResponse>
        ) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((balanceItem) => ({
                          ...balanceItem,
                          balance: bigIntParser(balanceItem.balance),
                          balance_24h: bigIntParser(balanceItem.balance_24h),
                          nft_data: balanceItem.nft_data
                              ? balanceItem.nft_data.map((nftItem) => ({
                                    ...nftItem,
                                    token_id: bigIntParser(nftItem.token_id),
                                }))
                              : null,
                      }))
                    : null;
            }

            return data;
        };

        return await this.execution.execute<NftAddressBalanceNftResponse>(
            endpoint,
            parseData
        );
    }

    /**
     *
     * Commonly used to verify ownership of NFTs (including ERC-721 and ERC-1155) within a collection.
     *
     * **Credit Cost**: 1 per call
     *
     * @param {string} chainName - The chain name eg: `eth-mainnet`.
     * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {string} collectionContract - The requested collection address.
     * @param {CheckOwnershipInNftQueryParamOpts} queryParamOpts
     *   - `traitsFilter`: Filters NFTs based on a specific trait. If this filter is used, the API will return all NFTs with the specified trait. Must be used with "values-filter", is case-sensitive, and requires proper URL encoding.
     *   - `valuesFilter`: Filters NFTs based on a specific trait value. If this filter is used, the API will return all NFTs with the specified trait value. Must be used with "traits-filter", is case-sensitive, and requires proper URL encoding.
     *
     */
    public async checkOwnershipInNft(
        chainName: Chain,
        walletAddress: string,
        collectionContract: string,
        queryParamOpts?: CheckOwnershipInNftQueryParamOpts
    ): Promise<GoldRushResponse<NftOwnershipForCollectionResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/address/${walletAddress}/collection/${collectionContract}`,
            [
                {
                    key: "traits-filter",
                    value: queryParamOpts?.traitsFilter,
                },
                {
                    key: "values-filter",
                    value: queryParamOpts?.valuesFilter,
                },
            ]
        );

        const parseData = (
            data: GoldRushResponse<NftOwnershipForCollectionResponse>
        ) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((nftItem) => ({
                          ...nftItem,
                          balance: bigIntParser(nftItem.balance),
                          balance_24h: bigIntParser(nftItem.balance_24h),
                          token_id: bigIntParser(nftItem.token_id),
                          nft_data: {
                              ...nftItem.nft_data!,
                              token_id: bigIntParser(
                                  nftItem.nft_data?.token_id
                              ),
                          },
                      }))
                    : null;
            }

            return data;
        };

        return await this.execution.execute<NftOwnershipForCollectionResponse>(
            endpoint,
            parseData
        );
    }

    /**
     *
     * Commonly used to verify ownership of a specific token (ERC-721 or ERC-1155) within a collection.
     *
     * **Credit Cost**: 1 per call
     *
     * @param {string} chainName - The chain name eg: `eth-mainnet`.
     * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {string} collectionContract - The requested collection address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {string} tokenId - The requested token ID.
     *
     */
    public async checkOwnershipInNftForSpecificTokenId(
        chainName: Chain,
        walletAddress: string,
        collectionContract: string,
        tokenId: string
    ): Promise<GoldRushResponse<NftOwnershipForCollectionResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/address/${walletAddress}/collection/${collectionContract}/token/${tokenId}`,
            []
        );

        const parseData = (
            data: GoldRushResponse<NftOwnershipForCollectionResponse>
        ) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((balanceItem) => ({
                          ...balanceItem,
                          balance: bigIntParser(balanceItem.balance),
                          balance_24h: bigIntParser(balanceItem.balance_24h),
                          token_id: bigIntParser(balanceItem.token_id),
                          last_transfered_at: balanceItem.last_transfered_at
                              ? new Date(balanceItem.last_transfered_at)
                              : null,
                      }))
                    : null;
            }

            return data;
        };

        return await this.execution.execute<NftOwnershipForCollectionResponse>(
            endpoint,
            parseData
        );
    }
}
